import {getTestProps} from '@benchling/js-test-utils';
import {Anchor, Button, Group, Paper, Stack, Text, createStyles} from '@mantine/core';
import React from 'react';

import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import type {ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

interface ModelCardProps {
  model: ScientificModelNode;
  onNewPrediction?: (model: ScientificModelNode) => void;
}

export default function ModelCard({model, onNewPrediction}: ModelCardProps): JSX.Element {
  const {classes} = useStyles();

  const handleNewPrediction = (): void => {
    onNewPrediction?.(model);
  };

  const handleReferenceClick = (event: ElementMouseEvent): void => {
    event.stopPropagation();
  };

  const handleReferenceKeyDown = (event: ElementKeyboardEvent): void => {
    event.stopPropagation();
  };

  return (
    <Paper
      aria-label={`New prediction with ${model.displayName}`}
      className={classes.card}
      p={18}
      radius="md"
      withBorder={true}
      {...getActionButtonRoleProps({onPress: handleNewPrediction})}
      {...getTestProps({component: 'ModelCard', element: 'root', key: model.externalReference})}
    >
      <Stack className={classes.cardContent} spacing={12}>
        <Group align="center" noWrap={true} spacing="sm">
          <div className={classes.iconBox}>
            <img alt={model.displayName} className={classes.icon} src={model.iconPath} />
          </div>
          <Stack spacing={4}>
            <Text fw={700} size="lg">
              {model.displayName}
            </Text>
            {model.paperLink ? (
              <Anchor
                className={classes.referenceLink}
                href={model.paperLink}
                onClick={handleReferenceClick}
                onKeyDown={handleReferenceKeyDown}
                rel="noopener noreferrer"
                size="sm"
                target="_blank"
              >
                <Group noWrap={true} spacing={4}>
                  <Text inherit={true}>Reference</Text>
                  <BenchlingIcon className={classes.referenceIcon} icon="arrow-up-right" />
                </Group>
              </Anchor>
            ) : null}
          </Stack>
        </Group>

        <Text className={classes.description} color="dimmed" size="sm">
          {model.description}
        </Text>

        <Button
          aria-hidden={true}
          className={classes.newPredictionButton}
          color="blue"
          component="div"
          mt="auto"
          radius="md"
          rightIcon={<BenchlingIcon icon="arrow-right" />}
          tabIndex={-1}
          variant="secondary"
        >
          New prediction
        </Button>
      </Stack>
    </Paper>
  );
}

const useStyles = createStyles((theme) => ({
  card: {
    borderColor: theme.colors.gray[1],
    cursor: 'pointer',
    height: '100%',
    transition: 'background-color 150ms ease',

    '&:hover': {
      backgroundColor: theme.fn.rgba(theme.colors.gray[1], 0.3),
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.fn.rgba(theme.colors.blue[6], 0.35)}`,
      outlineOffset: 2,
    },
  },
  cardContent: {
    height: '100%',
  },
  description: {
    lineHeight: 1.5,
    minHeight: 84,
  },
  icon: {
    maxHeight: '80%',
    maxWidth: '80%',
    objectFit: 'contain',
  },
  iconBox: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[0],
    borderRadius: theme.radius.md,
    display: 'flex',
    flex: '0 0 48px',
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  newPredictionButton: {
    alignSelf: 'center',
    pointerEvents: 'none',
  },
  referenceIcon: {
    fontSize: theme.fontSizes.xs,
  },
  referenceLink: {
    color: theme.colors.gray[6],
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));
